import I18n from 'I18n';
import { useCallback } from 'react';
import styled, { css } from 'styled-components';
import { LeftIcon, RightIcon } from 'foundations-assets';
import { Button } from 'foundations-components/transitional/components/Button';
import { Clickable } from 'foundations-components/transitional/components/Clickable';
import { ErrorMessage } from 'foundations-components/transitional/components/ErrorMessage';
import { IconButton } from 'foundations-components/transitional/components/IconButton';
import { TruncateString } from 'foundations-components/transitional/components/TruncateString';
import { getViewsByCatagoryQueryNameForCategory } from '../../standardViews/hooks/useFetchViewsByCategory';
import HubspotIcon from './HubspotIcon';
import { VIEW_CATEGORY_DEFAULT, getMessageForCategory } from '../ViewCategories';
import FormattedMessage from 'I18n/components/FormattedMessage';
import Raven from 'raven-js';
import { useGetIsCategoryColumnOpen, useSetIsCategoryColumnOpen } from '../hooks/useIsCategoryColumnOpen';
import { trackOpenViewFromAddViewPopover } from '../../tracking/viewsManagementTracking';
import { ViewBadge } from '../../viewName/components/ViewBadge';
import { useDebouncedFetchViewsByCategory } from '../hooks/useDebouncedFetchViewsByCategory';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const StyledColumnHeader = styled.div.withConfig({
  displayName: "ViewCategoryColumn__StyledColumnHeader",
  componentId: "sc-17eravt-0"
})(["&&&&{width:70px;height:100%;}"]);
const StyledColumnContent = styled.div.withConfig({
  displayName: "ViewCategoryColumn__StyledColumnContent",
  componentId: "sc-17eravt-1"
})(["&&&&{width:280px;}"]);
const StyledColumnCategory = styled.p.withConfig({
  displayName: "ViewCategoryColumn__StyledColumnCategory",
  componentId: "sc-17eravt-2"
})(["&&&&{top:", ";transform:rotate(90deg);width:225px;position:relative;left:-78px;font-weight:bold;}"], props => props.$default ? '100px' : '153px');
const StyledHubspotIconContainer = styled.div.withConfig({
  displayName: "ViewCategoryColumn__StyledHubspotIconContainer",
  componentId: "sc-17eravt-3"
})(({
  theme: _theme
}) => {
  const theme = _theme;
  return css(["&&&&{margin-left:", ";margin-bottom:", ";padding-top:", ";}"], theme.space['500'], theme.space['200'], theme.space['300']);
});
const StyledIconCollapsedContainer = styled(StyledHubspotIconContainer).withConfig({
  displayName: "ViewCategoryColumn__StyledIconCollapsedContainer",
  componentId: "sc-17eravt-4"
})(["&&&&{padding-top:", ";}"], props => props.$default ? '210px' : '261px');
const StyledIconExpandedContainer = styled.div.withConfig({
  displayName: "ViewCategoryColumn__StyledIconExpandedContainer",
  componentId: "sc-17eravt-5"
})(["&&&&{margin-left:auto;margin-right:10px;}"]);
const StyledUIFlex = styled.div.withConfig({
  displayName: "ViewCategoryColumn__StyledUIFlex",
  componentId: "sc-17eravt-6"
})(["&&&&{display:flex;align-items:flex-start;width:100%;justify-content:flex-start;max-width:100%;flex-grow:1;gap:10px;}"]);
const StyledColumnCategoryHeaderName = styled(StyledUIFlex).attrs({
  direction: 'row'
}).withConfig({
  displayName: "ViewCategoryColumn__StyledColumnCategoryHeaderName",
  componentId: "sc-17eravt-7"
})(["&&&&{padding-left:", ";padding-top:12px;padding-bottom:12px;border-bottom:1px solid ", ";font-weight:bold;background-color:", ";position:sticky;top:0;}"], props => props.$default ? '8px' : '24px', props => props.theme.color['border-core-default'], props => props.theme.color['fill-surface-raised']);
const StyledViewsListContent = styled.div.withConfig({
  displayName: "ViewCategoryColumn__StyledViewsListContent",
  componentId: "sc-17eravt-8"
})([""]);
const ViewItemRow = styled.div.withConfig({
  displayName: "ViewCategoryColumn__ViewItemRow",
  componentId: "sc-17eravt-9"
})(({
  theme: _theme
}) => {
  const theme = _theme;
  return css(["&&&&{display:flex;align-items:center;height:40px;padding-left:", ";}&&&&:hover{background-color:", ";}"], theme.space['900'], theme.color['fill-tertiary-hover']);
});
export const ViewCategoryColumn = ({
  objectTypeId,
  namespace,
  category,
  query,
  onChange
}) => {
  const {
    data: isCategoryColumnOpenData
  } = useGetIsCategoryColumnOpen({
    objectTypeId,
    namespace,
    category
  });
  const [toggleCategoryColumnOpen] = useSetIsCategoryColumnOpen({
    objectTypeId,
    namespace,
    category,
    isOpen: !isCategoryColumnOpenData || !isCategoryColumnOpenData.isCategoryColumnOpen
  });
  const handleClick = useCallback(update => {
    if (update && isCategoryColumnOpenData) {
      toggleCategoryColumnOpen().catch(e => {
        Raven.captureException(e, {
          extra: {
            objectTypeId,
            namespace,
            category
          }
        });
      });
    }
  }, [toggleCategoryColumnOpen, isCategoryColumnOpenData, objectTypeId, namespace, category]);
  const handleChange = useCallback(viewItem => {
    if (onChange) {
      trackOpenViewFromAddViewPopover({
        action: 'open view from addViewPopover',
        subAction: category,
        property: viewItem.name
      });
      onChange(viewItem);
    }
  }, [onChange, category]);
  const {
    data,
    loading,
    error,
    fetchMore
  } = useDebouncedFetchViewsByCategory({
    objectTypeId,
    query,
    namespace,
    category,
    count: 6
  });
  if (error) {
    return /*#__PURE__*/_jsx(ErrorMessage, {
      "data-test-id": "views-search-error",
      type: "400"
    });
  }
  if (!data || !isCategoryColumnOpenData) {
    return null;
  }
  const viewsByCategory = getViewsByCatagoryQueryNameForCategory(category);
  const {
    hasMore,
    offset,
    viewItems,
    total
  } = data[viewsByCategory];
  const handleLoadMoreClick = () => {
    fetchMore({
      variables: {
        offset
      }
    }).catch(e => {
      Raven.captureException(e, {
        extra: {
          objectTypeId,
          namespace,
          category
        }
      });
    });
  };
  const message = getMessageForCategory(category);
  const translatedMessage = I18n.text(message);
  const columnCategoryHeaderNameLabel = `${translatedMessage} (${total})`;
  const viewsListContent = viewItems.map(viewItem => {
    return /*#__PURE__*/_jsx(Clickable, {
      onClick: () => handleChange(viewItem),
      "data-test-id": `view-ui-clickable-${viewItem.id}`,
      children: /*#__PURE__*/_jsx(ViewItemRow, {
        children: /*#__PURE__*/_jsxs(TruncateString, {
          children: [viewItem.name, /*#__PURE__*/_jsx(ViewBadge, {
            viewId: viewItem.id
          })]
        })
      })
    }, viewItem.id);
  });
  const hasViews = viewItems.length > 0;
  return /*#__PURE__*/_jsx(_Fragment, {
    children: !isCategoryColumnOpenData.isCategoryColumnOpen || !hasViews ? /*#__PURE__*/_jsxs(StyledColumnHeader, {
      "data-test-id": `view-category-column-header-${category.toLowerCase()}`,
      children: [category === VIEW_CATEGORY_DEFAULT && /*#__PURE__*/_jsx(StyledHubspotIconContainer, {
        children: /*#__PURE__*/_jsx(HubspotIcon, {
          title: translatedMessage
        })
      }), /*#__PURE__*/_jsx(StyledColumnCategory, {
        $default: category === VIEW_CATEGORY_DEFAULT,
        children: columnCategoryHeaderNameLabel
      }), /*#__PURE__*/_jsx(StyledIconCollapsedContainer, {
        $default: category === VIEW_CATEGORY_DEFAULT,
        children: /*#__PURE__*/_jsx(IconButton, {
          tooltip: hasViews ? I18n.text('viewsManagement.addView.expand') : '',
          placement: "top",
          size: "extra-small",
          disabled: !hasViews,
          onClick: () => handleClick(hasViews),
          "data-test-id": `view-category-column-button-${category.toLowerCase()}`,
          children: /*#__PURE__*/_jsx(RightIcon, {})
        })
      })]
    }) : /*#__PURE__*/_jsxs(StyledColumnContent, {
      children: [/*#__PURE__*/_jsxs(StyledColumnCategoryHeaderName, {
        $default: category === VIEW_CATEGORY_DEFAULT,
        "data-test-id": `view-category-column-header-${category.toLowerCase()}-opened`,
        children: [category === VIEW_CATEGORY_DEFAULT && /*#__PURE__*/_jsx(HubspotIcon, {
          title: translatedMessage
        }), columnCategoryHeaderNameLabel, /*#__PURE__*/_jsx(StyledIconExpandedContainer, {
          children: /*#__PURE__*/_jsx(IconButton, {
            tooltip: I18n.text('viewsManagement.addView.collapse'),
            placement: "top",
            size: "extra-small",
            onClick: () => handleClick(true),
            "data-test-id": `view-category-column-button-${category.toLowerCase()}-opened`,
            children: /*#__PURE__*/_jsx(LeftIcon, {})
          })
        })]
      }), /*#__PURE__*/_jsx(StyledViewsListContent, {
        children: viewsListContent
      }), hasMore && /*#__PURE__*/_jsx(Button, {
        use: "transparent",
        onClick: handleLoadMoreClick,
        disabled: loading,
        "data-test-id": "view-category-load-more-button",
        marginInlineStart: "600",
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "viewsManagement.addView.loadMore"
        })
      })]
    })
  });
};